import { isSalaryPaymentType } from "./extras-salary-calc";

/**
 * VIB-2092: utbetalingsflyt for statister/eksterne.
 *
 * Ren logikk, ingen React eller Supabase. Kjernen er `paymentBlockers`: hva som
 * mangler før en statist kan meldes klar for utbetaling. Uten den sjekken kan PL
 * melde klar en person uten kontonummer, og DL oppdager det først når lønn skal
 * kjøres — da er PL for lengst videre på neste produksjon.
 *
 * Erik 29.09: PL melder klar på prosjektkortet, DL henter lista via Rapporter.
 */

export type PaymentState = "draft" | "ready_for_payment" | "paid";

/**
 * event_type-verdiene for audit-loggen. Navngitt her framfor å skrives som
 * litteraler på kallstedene, så de ikke kan divergere — nøklene MÅ finnes i
 * EVENT_TYPES (src/lib/event-registry.ts), og CI-testen der håndhever at alle
 * typer i prod har en oppføring.
 */
export const EXTRA_PAYMENT_EVENTS = {
  ready_for_payment: "extra_ready_for_payment",
  paid: "extra_marked_paid",
} as const;

export interface PaymentStateMeta {
  label: string;
  /** Tailwind-klasser for badgen. */
  badge: string;
}

/**
 * Badgefargene ligger bevisst utenfor KPI-paletten i widget-status.ts: dette er
 * ikke måloppnåelse, det er hvor langt en utbetaling har kommet. Grønt her betyr
 * «pengene er sendt», ikke «over målet».
 */
export const PAYMENT_STATES: Record<PaymentState, PaymentStateMeta> = {
  draft: { label: "Ikke klar", badge: "bg-slate-100 text-slate-600" },
  ready_for_payment: { label: "Klar for utbetaling", badge: "bg-amber-100 text-amber-700" },
  paid: { label: "Utbetalt", badge: "bg-green-100 text-green-700" },
};

export const PAYMENT_STATE_ORDER: PaymentState[] = ["ready_for_payment", "draft", "paid"];

export function isPaymentState(value: unknown): value is PaymentState {
  return typeof value === "string" && value in PAYMENT_STATES;
}

/** Feltene `paymentBlockers` trenger. Delmengde av ProjectExtra. */
export interface ExtraForPayment {
  full_name: string | null;
  name: string | null;
  personal_number: string | null;
  bank_account: string | null;
  agreed_amount: number | null;
  payment_type: string | null;
}

/**
 * Hva som mangler før statisten kan meldes klar for utbetaling.
 *
 * Tom liste = klar til å meldes. Teksten er skrevet for å vises rett til PL, så
 * den sier hvilket felt som mangler, ikke bare at «noe» mangler.
 *
 * Personnummer og kontonummer kreves KUN for lønnsmottakere
 * (`payment_type = 'lonnet'`). En ekstern som sender faktura skal ikke behøve å
 * oppgi dem, og å kreve dem ville blokkert en helt legitim utbetaling. Samme
 * skille som lønns-CSV-en (VIB-1283) gjør.
 */
export function paymentBlockers(extra: ExtraForPayment): string[] {
  const blockers: string[] = [];

  if (!(extra.full_name ?? extra.name ?? "").trim()) {
    blockers.push("navn");
  }
  if (!extra.agreed_amount || extra.agreed_amount <= 0) {
    blockers.push("avtalt beløp");
  }

  if (isSalaryPaymentType(extra.payment_type)) {
    if (!(extra.personal_number ?? "").trim()) blockers.push("personnummer");
    if (!(extra.bank_account ?? "").trim()) blockers.push("kontonummer");
  }

  return blockers;
}

export function canMarkReadyForPayment(extra: ExtraForPayment): boolean {
  return paymentBlockers(extra).length === 0;
}

/**
 * «navn og avtalt beløp» / «personnummer, kontonummer og avtalt beløp» — ferdig
 * setningsledd, så kallstedet slipper å bygge lister selv.
 */
export function formatBlockers(blockers: string[]): string {
  if (blockers.length === 0) return "";
  if (blockers.length === 1) return blockers[0];
  return `${blockers.slice(0, -1).join(", ")} og ${blockers[blockers.length - 1]}`;
}

export interface ReadySplit<T> {
  /** Kan meldes klare nå. */
  ready: T[];
  /** Mangler noe, med begrunnelsen per rad. */
  blocked: Array<{ extra: T; blockers: string[] }>;
}

/**
 * Deler et utvalg i de som kan meldes klare og de som mangler noe.
 *
 * Brukes av batch-handlingen: melder PL 20 statister klare, skal de 18 som er
 * komplette gå gjennom, og de 2 som mangler noe nevnes ved navn. Alt-eller-
 * ingenting ville gjort at én tom kontonummer-rad blokkerte hele batchen.
 */
export function splitByReadiness<T extends ExtraForPayment>(extras: T[]): ReadySplit<T> {
  const ready: T[] = [];
  const blocked: Array<{ extra: T; blockers: string[] }> = [];

  for (const extra of extras) {
    const blockers = paymentBlockers(extra);
    if (blockers.length === 0) ready.push(extra);
    else blocked.push({ extra, blockers });
  }

  return { ready, blocked };
}

/** Summen som faktisk skal utbetales for et utvalg. */
export function totalToPay(extras: Array<{ agreed_amount: number | null }>): number {
  return extras.reduce((sum, e) => sum + (e.agreed_amount ?? 0), 0);
}
